import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { getComicById, comics } from '@/mocks/comics';

function ArticlePage() {
  const { id = '' } = useParams<{ id: string }>();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsVisible(false);
    setTimeout(() => setIsVisible(true), 100);
  }, [id]);

  const comic = getComicById(id);
  const relatedComics = comics.filter((c) => c.id !== id && c.categorySlug === comic?.categorySlug).slice(0, 4);

  if (!comic) {
    return (
      <div className="w-full min-h-screen bg-background-50 flex flex-col items-center justify-center">
        <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mb-4">
          <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
        </div>
        <h1 className="text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
        <p className="text-foreground-600 mb-6">该漫画不存在或已被移除</p>
        <Link
          to="/category/all"
          className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i> 返回漫画列表
        </Link>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: comic.title,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    description: comic.description,
    url: `https://hanman.com/article/${comic.id}`,
    genre: comic.category,
    dateModified: comic.updateTime,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: 10,
      ratingCount: Math.floor(comic.views / 100),
    },
    interactionStatistic: {
      '@type': 'InteractionCounter',
      interactionType: 'https://schema.org/WatchAction',
      userInteractionCount: comic.views,
    },
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 韩漫漫画网`}
        description={`${comic.title}由${comic.author}创作，${comic.description}。韩漫漫画网提供${comic.title}在线免费阅读，全网最快更新，极速加载体验。`}
        keywords={`${comic.title},${comic.author},${comic.category}韩漫,韩漫在线阅读,${comic.tags.join(',')}`}
        canonicalUrl={`/article/${comic.id}`}
        ogType="article"
        ogImage={comic.cover}
        schema={articleSchema}
      />

      <main className="w-full bg-background-50 min-h-screen">
        <div className="bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6 pt-6 pb-8">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: `${comic.category}漫画`, path: `/category/${comic.categorySlug}` },
                { label: comic.title },
              ]}
            />
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">
          <div className={`flex flex-col md:flex-row gap-6 md:gap-8 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex-shrink-0 mx-auto md:mx-0">
              <div className="w-48 md:w-56 rounded-lg overflow-hidden">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - 韩漫封面`}
                  title={`${comic.title} - ${comic.author}作品`}
                  className="w-full h-auto object-cover object-top"
                  width={comic.coverWidth}
                  height={comic.coverHeight}
                />
              </div>
              <div className="flex items-center justify-center gap-1 mt-3 text-accent-500">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i
                    key={i}
                    className={`text-sm ${i < Math.floor(comic.rating / 2) ? 'ri-star-fill' : i < comic.rating / 2 ? 'ri-star-half-line' : 'ri-star-line'}`}
                  ></i>
                ))}
                <span className="text-sm font-semibold text-foreground-950 ml-1">{comic.rating}</span>
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mt-3">
                <span className="text-sm text-foreground-600">作者：<strong className="text-foreground-950">{comic.author}</strong></span>
                <span className="text-foreground-300">|</span>
                <Link
                  to={`/category/${comic.categorySlug}`}
                  className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                >
                  {comic.category}
                </Link>
                <span className="text-foreground-300">|</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${comic.status === '已完结' ? 'bg-secondary-100 text-secondary-900' : 'bg-primary-100 text-primary-700'} whitespace-nowrap`}>
                  {comic.status}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-3">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-full bg-background-100 text-xs text-foreground-600 whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-4 mt-4 text-sm text-foreground-600">
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万次阅读` : `${comic.views}次阅读`}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  更新于 {comic.updateTime}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-file-list-3-line"></i>
                  共 {comic.chapters.length} 话
                </span>
              </div>

              <div className="mt-5 p-4 rounded-xl bg-background-100">
                <h3 className="text-sm font-semibold text-foreground-950 mb-2">作品简介</h3>
                <p className="text-sm text-foreground-700 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex items-center gap-3 mt-5">
                <button className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-play-circle-line"></i>
                  从第1话开始阅读
                </button>
                <button className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-950 px-6 py-3 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line"></i>
                  收藏
                </button>
              </div>
            </div>
          </div>

          <section className="mt-10" aria-labelledby="chapters-title">
            <div className="flex items-center justify-between mb-4">
              <h2 id="chapters-title" className="text-lg md:text-xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                章节列表
              </h2>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 rounded-full bg-background-100 text-xs text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-sort-asc mr-1"></i>正序
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {comic.chapters.map((chapter, index) => (
                <Link
                  key={chapter.id}
                  to={`/article/${comic.id}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-background-100 hover:bg-primary-100/70 transition-colors cursor-pointer group"
                >
                  <span className="text-sm text-foreground-950 group-hover:text-primary-700 transition-colors line-clamp-1">
                    {chapter.title}
                  </span>
                  <span className="text-xs text-foreground-500 whitespace-nowrap ml-2">{chapter.updateTime}</span>
                </Link>
              ))}
            </div>
          </section>

          {relatedComics.length > 0 && (
            <section className="mt-12" aria-labelledby="related-title">
              <div className="flex items-center justify-between mb-4">
                <h2 id="related-title" className="text-lg md:text-xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  相关推荐
                </h2>
                <Link
                  to={`/category/${comic.categorySlug}`}
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  更多 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-product-shop>
                {relatedComics.map((rc) => (
                  <ComicCard key={rc.id} comic={rc} size="small" />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
}

export default ArticlePage;